import React, { useEffect, useRef, useCallback, useState } from 'react';
import ScheduledList from '../ScheduledList';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore, saveEmailPrefs } from '../../store';
import Sidebar, { SidebarRail } from '../Sidebar';
import EmailList from '../EmailList';
import EmailDetail from '../EmailDetail';
import ComposeNew from '../ComposeNew';
import Fab from '../Fab';
import { isEmailDrag } from '../../utils/emailDrag';
import { accentVars } from '../../utils/accountColors';
import { EMAIL_TAB, EMAIL_OPEN_MSG } from '../NewMailToast';
import i18n from '../../i18n';

// A new-mail pop-up was clicked (NewMailToast, Calem, report 2e5c8ac7): that mailbox's Inbox opens with the email
// selected, or a toast says it has left the Inbox meanwhile. From the ?open= of a new Email tab, and from the
// message the pop-up sends a tab that already shows Email (report b6182c13).
function openNotifiedEmail(openId: string, openAcct: string | null): Promise<void> {
  const s = useStore.getState();
  const acct = [openAcct, s.defaultAccountId].find(id => id && s.accounts.some(a => a.id === id));
  if (acct) {
    useStore.setState({ activeAccountId: acct, activeFolder: 'inbox', activeEmailId: null, searchQuery: '', emailLimit: 50 });
    s.loadLabelUnread();   // the sidebar's counts follow the mailbox, as on a click on it
  }
  return s.loadEmails().then(() => {
    const { emails, setActiveEmail, showToast } = useStore.getState();
    const row = emails.find(e => e.id === openId) ?? emails.find(e => (e.threadIds || []).includes(openId));
    if (row) setActiveEmail(row.id); else showToast(i18n.t('eml_notify_gone'));
  });
}

function DragHandle({ onDragStart }: { onDragStart: (e: React.MouseEvent) => void }) {
  return (
    <div
      onMouseDown={onDragStart}
      style={{
        width: 4, flexShrink: 0, cursor: 'col-resize', background: 'var(--border)',
        transition: 'background 0.15s', position: 'relative', zIndex: 1,
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--accent)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'var(--border)')}
    />
  );
}

// PandaAI Email module — the three-column mail client (gated by the 'email' module).
// Reusable view — used by BOTH the standalone module page (/m/email) and the Portal dashboard's
// Email tab (same code, same design). Fills its flex parent, the XView pattern PettyCashView /
// BankStatementView / CalendarView already use.
export function MailAppView() {
  const { t } = useTranslation();
  const { loadAccounts, loadEmails, backgroundRefresh, activeEmailId, setActiveEmail, accounts, activeAccountId, activeFolder, loadEmailPrefs, emailPrefsVersion } = useStore();
  // The Scheduled folder is not an emails query — it lists scheduled_emails rows (ScheduledList).
  const ListPane = activeFolder === 'scheduled' ? ScheduledList : EmailList;
  // The open mailbox's colour replaces the app-wide teal for the whole module: New Email, the
  // switches, the count bubbles, the reading pane, and the compose windows and pop-ups (they render
  // inside this element, so they inherit it too). Teal while no mailbox is open.
  const accent = accentVars(accounts.find(a => a.id === activeAccountId)?.color);

  // Column widths — wider default list for comfortable reading; drag adjustments persist.
  const [sidebarWidth, setSidebarWidth] = useState(() => { try { return Number(localStorage.getItem('mf_w_sidebar')) || 220; } catch { return 220; } });
  const [listWidth, setListWidth] = useState(() => { try { return Number(localStorage.getItem('mf_w_list')) || 380; } catch { return 380; } });
  // Gmail-style sidebar: collapsed to an icon rail by default; hovering the rail overlays the full
  // sidebar; the pin button keeps it permanently open. Choice persists.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => { try { return localStorage.getItem('mf_sidebarCollapsed') !== '0'; } catch { return true; } });
  // Split-screen on (default, matches prior behaviour): list + detail side by side. Off: the list
  // takes the full column and clicking an email swaps it out for the detail pane. Choice persists.
  const [splitScreen, setSplitScreenState] = useState<boolean>(() => { try { return localStorage.getItem('mf_splitScreen') !== '0'; } catch { return true; } });
  const setSplitScreen = useCallback((v: boolean) => {
    setSplitScreenState(v);
    try { localStorage.setItem('mf_splitScreen', v ? '1' : '0'); } catch { /* ignore */ }
    saveEmailPrefs({ splitScreen: v });
  }, []);
  // The saved layout arrived from the server (loadEmailPrefs wrote it into the keys above) — use it.
  useEffect(() => {
    if (!emailPrefsVersion) return;
    try {
      setSidebarWidth(Number(localStorage.getItem('mf_w_sidebar')) || 220);
      setListWidth(Number(localStorage.getItem('mf_w_list')) || 380);
      setSidebarCollapsed(localStorage.getItem('mf_sidebarCollapsed') !== '0');
      setSplitScreenState(localStorage.getItem('mf_splitScreen') !== '0');
    } catch { /* ignore */ }
  }, [emailPrefsVersion]);
  // Split screen: the strip across the top of the list AND the reading pane that the list draws its
  // toolbar into (Select all, the switches, Search, the bulk actions) — full width as with split screen
  // off, not squeezed onto two rows in the list column (Calem, Bugify #e0e30d4b). Kept in state, so the
  // list moves its toolbar there as soon as the strip exists.
  const [toolbarHost, setToolbarHost] = useState<HTMLDivElement | null>(null);
  const [sidebarHover, setSidebarHover] = useState(false);
  const [sidebarModal, setSidebarModal] = useState(false);   // keep the overlay mounted while its modal is open
  // New Email from the floating "+" — its own composer, as the sidebar's and the rail's buttons each have.
  const [showCompose, setShowCompose] = useState(false);
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(c => {
      const n = !c;
      try { localStorage.setItem('mf_sidebarCollapsed', n ? '1' : '0'); } catch { /* ignore */ }
      saveEmailPrefs({ sidebarCollapsed: n });
      if (!n) setSidebarHover(false);
      return n;
    });
  }, []);
  // An email dragged from the list opens the overlay as well (mouse hover doesn't fire mid-drag), so
  // it can be dropped on a label; it closes once the drag moves off the rail + overlay. Leave also
  // fires between their child elements, hence checking what is now under the pointer.
  const railDragEnter = (e: React.DragEvent) => { if (isEmailDrag(e)) setSidebarHover(true); };
  const railDragLeave = (e: React.DragEvent) => {
    if (isEmailDrag(e) && !e.currentTarget.contains(document.elementFromPoint(e.clientX, e.clientY))) setSidebarHover(false);
  };
  const dragging = useRef<{ col: 'sidebar' | 'list'; startX: number; startWidth: number; width?: number } | null>(null);

  const startDrag = useCallback((col: 'sidebar' | 'list', startWidth: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = { col, startX: e.clientX, startWidth };
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      const dx = ev.clientX - dragging.current.startX;
      // The list↔detail divider can be dragged out to half the page; the sidebar divider keeps
      // its narrower cap (it isn't a reading column).
      const max = dragging.current.col === 'list' ? window.innerWidth * 0.5 : 500;
      const newW = Math.max(160, Math.min(max, dragging.current.startWidth + dx));
      if (dragging.current.col === 'sidebar') setSidebarWidth(newW);
      else setListWidth(newW);
      try { localStorage.setItem(dragging.current.col === 'sidebar' ? 'mf_w_sidebar' : 'mf_w_list', String(newW)); } catch { /* ignore */ }
      dragging.current.width = newW;
    };
    const onUp = () => {
      const d = dragging.current;
      if (d?.width) saveEmailPrefs({ widths: d.col === 'sidebar' ? { sidebar: Math.round(d.width) } : { list: Math.round(d.width) } });
      dragging.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Initial load: local mail shows instantly; new mail is fetched behind it and merged silently. The
  // saved layout comes first, so the mail opens on the saved default mailbox and conversation view.
  // ?open=<email id>&account=<its mailbox> — a new-mail pop-up was clicked while no tab showed Email
  // (openNotifiedEmail). The parameters leave the address bar at once, as Gmail's do below.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    const openAcct = params.get('account');
    if (openId) {
      params.delete('open');
      params.delete('account');
      const rest = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    loadEmailPrefs().then(() => loadAccounts()).then(() => {
      if (!openId) return loadEmails();
      return openNotifiedEmail(openId, openAcct);
    }).then(() => {
      const { activeAccountId, syncAccount } = useStore.getState();
      if (activeAccountId) syncAccount(activeAccountId, { auto: true }).catch(() => {});
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // While it shows, this tab is the Email tab (Calem, report b6182c13: "default back to the tab that already has
  // email open rather than opening a new tab"): it answers to EMAIL_TAB, so a new-mail pop-up clicked in another
  // tab brings this one to the front and sends it the email (NewMailToast). The name goes with the view, and
  // while the page is away (pagehide), so a page this tab moves on to doesn't answer to it.
  useEffect(() => {
    const claim = () => { window.name = EMAIL_TAB; };
    const release = () => { if (window.name === EMAIL_TAB) window.name = ''; };
    const onMessage = (e: MessageEvent) => {
      const m = e.data;
      if (e.origin !== window.location.origin || m?.type !== EMAIL_OPEN_MSG || typeof m.open !== 'string') return;
      openNotifiedEmail(m.open, typeof m.account === 'string' ? m.account : null).catch(() => {});
    };
    claim();
    window.addEventListener('pageshow', claim);
    window.addEventListener('pagehide', release);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('pageshow', claim);
      window.removeEventListener('pagehide', release);
      window.removeEventListener('message', onMessage);
      release();
    };
  }, []);

  // Handle Gmail OAuth callback redirect (?gmail_success / ?gmail_error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_success')) {
      window.history.replaceState({}, '', window.location.pathname);
      loadAccounts().then(() => loadEmails());
    }
    if (params.get('gmail_error')) {
      window.history.replaceState({}, '', window.location.pathname);
      console.error('Gmail OAuth error:', decodeURIComponent(params.get('gmail_error') || ''));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Background refresh every 60s — silent, skipped while composing.
  useEffect(() => {
    const timer = setInterval(() => { backgroundRefresh().catch(() => {}); }, 60_000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The page is white, --bg-surface, as on every page of the top menu (Calem, report ddf1ba0e, 3rd opening: "Change
  // every page background to white"). It was the app's beige, then for Bugify #b07c8dae's 1st opening the top menu's
  // Email box wash one shade lighter, and for its 2nd the list's grey, --c-gray-50. The sidebar, the list and the
  // reading pane paint their own colours over it.
  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', background: 'var(--bg-surface)', ...accent }}>
      {sidebarCollapsed ? (
        <div style={{ position: 'relative', width: 56, flexShrink: 0, display: 'flex' }}
          onMouseEnter={() => setSidebarHover(true)} onMouseLeave={() => setSidebarHover(false)}
          onDragEnter={railDragEnter} onDragLeave={railDragLeave}>
          <SidebarRail onExpand={toggleSidebar} />
          {(sidebarHover || sidebarModal) && (
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 280, zIndex: 300, display: 'flex', boxShadow: '6px 0 24px rgba(0,0,0,0.22)' }}>
              <Sidebar collapsed onToggleCollapse={toggleSidebar} onModalState={setSidebarModal} />
            </div>
          )}
        </div>
      ) : (<>
        <div style={{ width: sidebarWidth, flexShrink: 0, overflow: 'hidden', display: 'flex' }}>
          <Sidebar collapsed={false} onToggleCollapse={toggleSidebar} />
        </div>
        <DragHandle onDragStart={startDrag('sidebar', sidebarWidth)} />
      </>)}
      {splitScreen ? (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div ref={setToolbarHost} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
            <div style={{ width: listWidth, flexShrink: 0, overflow: 'hidden', display: 'flex' }}>
              <ListPane splitScreen onSetSplitScreen={setSplitScreen} toolbarHost={toolbarHost} />
            </div>
            <DragHandle onDragStart={startDrag('list', listWidth)} />
            <EmailDetail />
          </div>
        </div>
      ) : activeEmailId ? (
        <EmailDetail onBack={() => setActiveEmail(null)} />
      ) : (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          <ListPane splitScreen={false} onSetSplitScreen={setSplitScreen} />
        </div>
      )}
      {/* The floating bottom-right "+" every list module has (Calem) — here it opens New Email. It wears this
          element's var(--accent), the open mailbox's colour like New Email's (Calem 2026-10-01, Bugify #87ed6d43:
          "Change the email theme color to the email client color"); from Bugify #7568b3b5 (2026-09-27) until then it
          wore the top menu's Email button colour, the fixed purple, whichever mailbox was open. */}
      <Fab onClick={() => setShowCompose(true)} title={t('eml_new_email')} color="var(--accent)" />
      {showCompose && <ComposeNew onClose={() => setShowCompose(false)} />}
    </div>
  );
}

// ── Standalone module page (/m/email): brand header + the shared view ────────
export default function MailApp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title="SLF" style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_email')}</div>
      </header>
      <MailAppView />
    </div>
  );
}
